import React, { useState, useEffect } from "react";
import { Box, Button, IconButton, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { type GridCellEditCommitParams } from "@mui/x-data-grid-pro";
import type { AdditionalFieldType } from "@/types/additionalFields";
import { renderAdditionalFieldInput } from "@/features/map/utils/renderAdditionalFieldInput";

export interface EditableCellProps {
  value: string | number | null;
  displayValue?: string | number | null;
  rowId: number;
  field: string;
  type?: "text" | "number";
  fieldType?: AdditionalFieldType;
  onEdit: (params: GridCellEditCommitParams) => void;
  disabled?: boolean;
  multiline?: boolean;
  maxLength?: number;
  onEditingChange?: (rowId: number, field: string, isEditing: boolean) => void;
}

/**
 * 編集可能なセルコンポーネント
 * ホバー時に編集アイコンを表示し、クリックで編集モードに切り替わる
 *
 * @param value - 表示する値
 * @param rowId - 行ID
 * @param field - フィールド名
 * @param type - 入力タイプ（text または number）
 * @param onEdit - 編集完了時のコールバック関数
 */
export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  displayValue,
  rowId,
  field,
  type = "text",
  fieldType,
  onEdit,
  disabled = false,
  multiline = false,
  maxLength,
  onEditingChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? "");
  const [isHovered, setIsHovered] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // 追加項目の場合はfieldTypeを使用（日付、日時などの詳細な型情報）
  // 通常の列の場合はtypeから推測（テキストまたは数値）
  const effectiveFieldType =
    fieldType ??
    (multiline ? "textarea" : type === "number" ? "number" : "text");
  const displayText = displayValue !== undefined ? displayValue : value;

  // valueプロップが変更されたらeditValueを更新
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value ?? "");
      setValidationError(null);
    }
  }, [value, isEditing]);

  // 数値フィールドのバリデーション（15桁まで）
  const validateNumberField = (val: string | number): string | null => {
    if (effectiveFieldType !== "number") return null;
    if (!val || val === "") return null;

    const numStr = String(val).replace(/^-/, "");
    if (numStr.length > 15) {
      return "15桁以内で入力してください";
    }
    return null;
  };

  // editValueが変更されたらバリデーションを実行
  useEffect(() => {
    if (isEditing) {
      const error = validateNumberField(editValue);
      setValidationError(error);
    }
  }, [editValue, isEditing, effectiveFieldType]);

  /**
   * 編集内容を保存する
   * 値が変更されている場合のみ、onEditコールバックを呼び出す
   */
  const handleSave = (): void => {
    // バリデーションエラーがある場合は保存しない
    if (validationError) {
      return;
    }

    setIsEditing(false);
    onEditingChange?.(rowId, field, false);
    if (editValue !== value) {
      const params: GridCellEditCommitParams = {
        id: rowId,
        field,
        value: type === "number" ? Number(editValue) : editValue,
      };
      onEdit(params);
    }
  };

  /**
   * 編集をキャンセルする
   */
  const handleCancel = (): void => {
    setEditValue(value ?? "");
    setIsEditing(false);
    onEditingChange?.(rowId, field, false);
  };

  /**
   * 編集モード開始
   */
  const handleStartEdit = (): void => {
    setIsEditing(true);
    onEditingChange?.(rowId, field, true);
  };

  if (isEditing) {
    // 日付・日時フィールドの場合は横並びレイアウト
    const isDateField =
      effectiveFieldType === "date" || effectiveFieldType === "datetime";

    if (isDateField) {
      return (
        <Box sx={{ width: "100%", py: 1, px: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mb: 1 }}
            flexWrap="nowrap"
          >
            <Button
              size="medium"
              variant="outlined"
              onClick={handleCancel}
              sx={{ minWidth: "90px", whiteSpace: "nowrap" }}
            >
              キャンセル
            </Button>
            <Button
              size="medium"
              variant="contained"
              onClick={handleSave}
              disabled={!!validationError}
              sx={{ minWidth: "70px", whiteSpace: "nowrap" }}
            >
              保存
            </Button>
          </Stack>
          {renderAdditionalFieldInput({
            fieldType: effectiveFieldType,
            value: editValue,
            onChange: setEditValue,
            onKeyDown: (e) => {
              // 矢印キーの場合、イベントの伝播を止める(テキスト入力フォーム内でのカーソル移動を可能にする)
              if (
                ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
                  e.key
                )
              ) {
                e.stopPropagation();
              }
            },
            autoFocus: true,
            size: "small",
            fullWidth: true,
            helperText: validationError ?? undefined,
          })}
        </Box>
      );
    }

    // その他のフィールドは従来通り縦並びレイアウト
    return (
      <Box sx={{ width: "100%", py: 1, px: 1 }}>
        {renderAdditionalFieldInput({
          fieldType: effectiveFieldType,
          value: editValue,
          onChange: setEditValue,
          onKeyDown: (e) => {
            // 矢印キーの場合、イベントの伝播を止める(テキスト入力フォーム内でのカーソル移動を可能にする)
            if (
              ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
                e.key
              )
            ) {
              e.stopPropagation();
            }
          },
          autoFocus: true,
          size: "small",
          fullWidth: true,
          maxLength,
          helperText: validationError ?? undefined,
          sx: { mb: 1 },
        })}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" variant="outlined" onClick={handleCancel}>
            キャンセル
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={!!validationError}
          >
            保存
          </Button>
        </Stack>
      </Box>
    );
  }

  if (disabled) {
    return (
      <Box sx={{ textAlign: "left", width: "100%", py: 1 }}>
        {displayText ?? ""}
      </Box>
    );
  }

  return (
    <Box
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        width: "100%",
        height: "100%",
        minHeight: "40px", // 空の値でもホバー領域を確保
        py: 1,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflow: "hidden",
          wordBreak: "break-word",
          whiteSpace: multiline ? "pre-wrap" : "normal",
        }}
      >
        {displayText ?? ""}
      </Box>
      {isHovered && (
        <IconButton
          size="small"
          onClick={handleStartEdit}
          sx={{ flexShrink: 0 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
